import { useCallback, useEffect, useRef } from 'react';

const IDLE_MS = 1000;

/** Attach idle-hiding overlay scrollbar behavior to a scrollable element. */
export function attachOverlayScrollbar(el) {
  if (!el || el.dataset.overlayScrollbar) return () => {};

  el.dataset.overlayScrollbar = '1';
  el.classList.add('overlay-scrollbar');

  let timeoutId;

  const onScroll = () => {
    el.classList.add('overlay-scrollbar-active');
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      el.classList.remove('overlay-scrollbar-active');
    }, IDLE_MS);
  };

  const onEnter = () => el.classList.add('overlay-scrollbar-hover');
  const onLeave = () => el.classList.remove('overlay-scrollbar-hover');

  el.addEventListener('scroll', onScroll, { passive: true });
  el.addEventListener('mouseenter', onEnter);
  el.addEventListener('mouseleave', onLeave);

  return () => {
    window.clearTimeout(timeoutId);
    el.removeEventListener('scroll', onScroll);
    el.removeEventListener('mouseenter', onEnter);
    el.removeEventListener('mouseleave', onLeave);
    delete el.dataset.overlayScrollbar;
    el.classList.remove('overlay-scrollbar', 'overlay-scrollbar-active', 'overlay-scrollbar-hover');
  };
}

/** Ref callback for scrollable elements that should use overlay scrollbars. */
export function useOverlayScrollbarRef() {
  const cleanupRef = useRef(null);

  const ref = useCallback((el) => {
    cleanupRef.current?.();
    cleanupRef.current = el ? attachOverlayScrollbar(el) : null;
  }, []);

  useEffect(() => () => cleanupRef.current?.(), []);

  return ref;
}
